import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdBusiness, MdLock, MdLocationCity, MdEmail, MdPhone, MdPerson } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

export default function AddCompanyModal({ onClose, onSuccess }) {
    const { t } = useLanguage();
    const [form, setForm] = useState({
        name: '',
        il: '',
        ilce: '',
        email: '',
        telNo: '',
        username: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = t('addCompanyModal.companyRequired');
        if (!form.il.trim()) errs.il = t('addCompanyModal.provinceRequired');
        if (!form.ilce.trim()) errs.ilce = t('addCompanyModal.districtRequired');
        if (!form.username.trim()) errs.username = t('addCompanyModal.usernameRequired');
        else if (form.username.trim().length < 3) errs.username = t('addCompanyModal.usernameMin');
        if (!form.password.trim()) errs.password = t('addCompanyModal.passwordRequired');
        else if (form.password.length < 6) errs.password = t('addCompanyModal.passwordMin');

        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            errs.email = t('addCompanyModal.emailInvalid');
        }
        return errs;
    };

    const handleChange = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
        setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }

        const address = `${form.il.trim()} / ${form.ilce.trim()}`;

        try {
            setLoading(true);
            await api.post('/companies', {
                name: form.name.trim(),
                address,
                email: form.email.trim(),
                telNo: form.telNo.trim(),
                username: form.username.trim(),
                password: form.password
            });
            toast.success(t('addCompanyModal.companyCreated'));
            onSuccess();
        } catch (err) {
            const msg = err.response?.data?.message || t('addCompanyModal.createFailed');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const iconStyle = {
        position: 'absolute', left: 12, top: '50%',
        transform: 'translateY(-50%)', color: 'var(--text-muted)',
        fontSize: 17, zIndex: 1, pointerEvents: 'none'
    };

    return (
        <AnimatePresence>
            <motion.div
                className="modal-overlay"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={(e) => e.target === e.currentTarget && onClose()}
            >
                <motion.div
                    className="modal"
                    initial={{ opacity: 0, scale: 0.9, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                >
                    <div className="modal-header" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 16 }}>
                        <div className="modal-title">
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(79,142,247,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                                <MdBusiness />
                            </div>
                            {t('addCompanyModal.title')}
                        </div>
                        <button className="modal-close" onClick={onClose} id="close-modal-btn"><MdClose /></button>
                    </div>

                    <form onSubmit={handleSubmit} autoComplete="off">
                        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>

                            {/* Firma Adı — full width */}
                            <div className="form-group">
                                <label className="form-label" htmlFor="company-name">{t('addCompanyModal.companyName')}</label>
                                <div style={{ position: 'relative' }}>
                                    <span style={iconStyle}><MdBusiness /></span>
                                    <input
                                        id="company-name"
                                        className="form-input"
                                        type="text"
                                        placeholder={t('addCompanyModal.companyNamePlaceholder')}
                                        value={form.name}
                                        onChange={handleChange('name')}
                                        style={{ paddingLeft: 38 }}
                                        disabled={loading}
                                        autoComplete="off"
                                    />
                                </div>
                                {errors.name && <div className="form-error">{errors.name}</div>}
                            </div>

                            {/* İl + İlçe */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="company-il">{t('addCompanyModal.province')}</label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={iconStyle}><MdLocationCity /></span>
                                        <input
                                            id="company-il"
                                            className="form-input"
                                            type="text"
                                            placeholder={t('addCompanyModal.provincePlaceholder')}
                                            value={form.il}
                                            onChange={handleChange('il')}
                                            style={{ paddingLeft: 38 }}
                                            disabled={loading}
                                            autoComplete="off"
                                        />
                                    </div>
                                    {errors.il && <div className="form-error">{errors.il}</div>}
                                </div>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="company-ilce">{t('addCompanyModal.district')}</label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={iconStyle}><MdLocationCity /></span>
                                        <input
                                            id="company-ilce"
                                            className="form-input"
                                            type="text"
                                            placeholder={t('addCompanyModal.districtPlaceholder')}
                                            value={form.ilce}
                                            onChange={handleChange('ilce')}
                                            style={{ paddingLeft: 38 }}
                                            disabled={loading}
                                            autoComplete="off"
                                        />
                                    </div>
                                    {errors.ilce && <div className="form-error">{errors.ilce}</div>}
                                </div>
                            </div>

                            {/* E-posta + Telefon */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="company-email">{t('addCompanyModal.email')}</label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={iconStyle}><MdEmail /></span>
                                        <input
                                            id="company-email"
                                            className="form-input"
                                            type="email"
                                            placeholder={t('addCompanyModal.emailPlaceholder')}
                                            value={form.email}
                                            onChange={handleChange('email')}
                                            style={{ paddingLeft: 38 }}
                                            disabled={loading}
                                            autoComplete="off"
                                        />
                                    </div>
                                    {errors.email && <div className="form-error">{errors.email}</div>}
                                </div>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="company-tel">{t('addCompanyModal.phone')}</label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={iconStyle}><MdPhone /></span>
                                        <input
                                            id="company-tel"
                                            className="form-input"
                                            type="tel"
                                            placeholder={t('addCompanyModal.phonePlaceholder')}
                                            value={form.telNo}
                                            onChange={handleChange('telNo')}
                                            style={{ paddingLeft: 38 }}
                                            disabled={loading}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="divider" style={{ margin: '8px 0 16px', background: 'rgba(255,255,255,0.05)' }} />
                            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ color: 'var(--primary)' }}>💡</span> {t('addCompanyModal.accountInfo')}
                            </p>

                            {/* Kullanıcı adı + Şifre */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="company-username">{t('addCompanyModal.username')}</label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={iconStyle}><MdPerson /></span>
                                        <input
                                            id="company-username"
                                            className="form-input"
                                            type="text"
                                            placeholder={t('addCompanyModal.usernamePlaceholder')}
                                            value={form.username}
                                            onChange={handleChange('username')}
                                            style={{ paddingLeft: 38 }}
                                            disabled={loading}
                                            autoComplete="off"
                                        />
                                    </div>
                                    {errors.username && <div className="form-error">{errors.username}</div>}
                                </div>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="company-password">{t('addCompanyModal.loginPassword')}</label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={iconStyle}><MdLock /></span>
                                        <input
                                            id="company-password"
                                            className="form-input"
                                            type="password"
                                            placeholder={t('addCompanyModal.passwordPlaceholder')}
                                            value={form.password}
                                            onChange={handleChange('password')}
                                            style={{ paddingLeft: 38 }}
                                            disabled={loading}
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    {errors.password && <div className="form-error">{errors.password}</div>}
                                </div>
                            </div>

                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: 8 }}>
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading} id="cancel-btn">
                                {t('common.cancel')}
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading} id="submit-company-btn">
                                {loading ? <><span className="spinner spinner-sm" /> {t('common.creating')}</> : t('addCompanyModal.createCompany')}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
